import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Truck, Search, Download, Bell, Mail, AlertTriangle, Package, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  escrow_paid: "Payée (séquestre)",
  shipped: "Expédiée",
  delivered: "Livrée",
  escrow_received: "Réceptionnée",
  released: "Fonds libérés",
  disputed: "Litige",
  refunded: "Remboursée",
};

const STATUS_COLORS = {
  escrow_paid: "bg-[color:var(--color-yellow)]",
  shipped: "bg-[color:var(--color-mint)]",
  delivered: "bg-[color:var(--color-mint)]",
  escrow_received: "bg-[color:var(--color-mint)]",
  released: "bg-black text-white",
  disputed: "bg-[color:var(--color-coral)] text-white",
  refunded: "bg-gray-200",
};

export default function Logistics() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [mrOpen, setMrOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!loading && (!user || !["logistics", "admin"].includes(user.role))) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    (async () => {
      try {
        const q = new URLSearchParams();
        if (statusFilter) q.set("status", statusFilter);
        if (search) q.set("search", search);
        q.set("limit", "100");
        const [oRes, sRes] = await Promise.all([
          api.get(`/logistics/orders?${q.toString()}`),
          api.get(`/logistics/stats`),
        ]);
        if (cancel) return;
        setOrders(oRes.data.orders || []);
        setStats(sRes.data);
      } catch (e) {
        console.error("Logistics load failed:", e);
        if (!cancel) toast.error("Erreur de chargement");
      }
    })();
    return () => { cancel = true; };
  }, [user, statusFilter, search, refresh]);

  const reload = () => setRefresh(n => n + 1);

  const downloadCsv = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/logistics/export.csv${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fripri-orders-${statusFilter || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Échec de l'export"); }
  };

  if (!user) return <div className="p-20 text-center">Chargement…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 has-bottom-nav" data-testid="logistics-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-black text-[color:var(--color-mint)] flex items-center justify-center">
            <Truck size={32} strokeWidth={2.5}/>
          </div>
          <div>
            <p className="label">Back-office</p>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tighter">Logistique</h1>
            <p className="text-xs text-[color:var(--color-text-muted)]">Suivi des commandes, expéditions & Mondial Relay</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadCsv} className="btn btn-sm border-2 border-black bg-white hover:bg-black hover:text-white" data-testid="logistics-export-btn">
            <Download size={14} strokeWidth={3}/> CSV
          </button>
          <button onClick={() => { setSelected(null); setMrOpen(true); }} className="btn btn-primary btn-sm" data-testid="contact-mr-btn">
            <Mail size={14} strokeWidth={3}/> Contacter Mondial Relay
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          <StatCard label="Total" value={stats.total} color="bg-white" icon={<Package size={18} strokeWidth={2.5}/>}/>
          <StatCard label="En séquestre" value={stats.by_status?.escrow_paid || 0} color="bg-[color:var(--color-yellow)]" icon={<Clock size={18} strokeWidth={2.5}/>}/>
          <StatCard label="Expédiées" value={stats.by_status?.shipped || 0} color="bg-[color:var(--color-mint)]" icon={<Truck size={18} strokeWidth={2.5}/>}/>
          <StatCard label="Livrées" value={stats.by_status?.delivered || 0} color="bg-white" icon={<CheckCircle2 size={18} strokeWidth={2.5}/>}/>
          <StatCard label="Retards >3j" value={stats.late_shipments || 0} color="bg-[color:var(--color-coral)] text-white" icon={<AlertTriangle size={18} strokeWidth={2.5}/>}/>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/50"/>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="N° commande, tracking, email…"
            className="input w-full pl-9"
            data-testid="logistics-search"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input" data-testid="status-filter">
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Orders table */}
      <div className="card border-2 border-black overflow-x-auto">
        <table className="w-full text-sm" data-testid="logistics-orders-table">
          <thead className="bg-black text-white">
            <tr>
              <th className="text-left p-3 label text-white">Commande</th>
              <th className="text-left p-3 label text-white">Statut</th>
              <th className="text-left p-3 label text-white">Acheteur</th>
              <th className="text-left p-3 label text-white">Vendeur(s)</th>
              <th className="text-right p-3 label text-white">Montant</th>
              <th className="text-left p-3 label text-white">Tracking</th>
              <th className="text-left p-3 label text-white">Créée</th>
              <th className="text-right p-3 label text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={8} className="p-12 text-center text-[color:var(--color-text-muted)]">Aucune commande {statusFilter ? `(filtre: ${STATUS_LABELS[statusFilter]})` : ""}</td></tr>
            ) : orders.map((o) => (
              <tr key={o.order_id} className="border-t border-black/10 hover:bg-black/5">
                <td className="p-3 font-mono text-xs">{o.order_id}</td>
                <td className="p-3"><span className={`pill text-[10px] ${STATUS_COLORS[o.status] || "bg-gray-100"}`}>{STATUS_LABELS[o.status] || o.status}</span></td>
                <td className="p-3 text-xs">{o.buyer?.name || o.buyer?.email || "—"}</td>
                <td className="p-3 text-xs">{(o.sellers || []).map(s => s?.name || s?.email).filter(Boolean).join(", ") || "—"}</td>
                <td className="p-3 text-right font-bold">{(o.amount || 0).toFixed(2)} €</td>
                <td className="p-3 text-xs font-mono">{o.tracking_number || "—"}</td>
                <td className="p-3 text-xs">{(o.created_at || "").slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setSelected(o)} className="text-xs underline hover:no-underline" data-testid={`open-order-${o.order_id}`}>
                    Gérer →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <OrderDrawer order={selected} onClose={() => setSelected(null)} onSaved={reload} onContactMr={() => { setMrOpen(true); }} />
      )}
      {mrOpen && (
        <MondialRelayModal order={selected} onClose={() => setMrOpen(false)} />
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon }) {
  return (
    <div className={`card border-2 border-black p-3 ${color}`}>
      <div className="flex items-center justify-between">
        <p className="label text-xs">{label}</p>
        {icon}
      </div>
      <p className="text-3xl font-black mt-2">{value}</p>
    </div>
  );
}

function OrderDrawer({ order, onClose, onSaved, onContactMr }) {
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.shipping_carrier || "Mondial Relay");
  const [notes, setNotes] = useState(order.logistics_notes || "");
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyBody, setNotifyBody] = useState("");
  const [notifyTarget, setNotifyTarget] = useState("buyer");
  const [notifying, setNotifying] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { tracking_number: tracking, shipping_carrier: carrier, logistics_notes: notes };
      if (newStatus) payload.status = newStatus;
      await api.patch(`/logistics/orders/${order.order_id}`, payload);
      toast.success("Mise à jour enregistrée ✓");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const sendNotif = async () => {
    if (!notifyTitle || !notifyBody) { toast.error("Titre + message requis"); return; }
    setNotifying(true);
    try {
      await api.post(`/logistics/orders/${order.order_id}/notify`, { target: notifyTarget, title: notifyTitle, body: notifyBody });
      toast.success(`Notification envoyée → ${notifyTarget === "buyer" ? "acheteur" : "vendeur(s)"}`);
      setNotifyTitle(""); setNotifyBody("");
    } catch (e) { toast.error("Erreur"); }
    finally { setNotifying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose} data-testid="order-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl h-full overflow-y-auto p-6 lg:p-8 border-l-2 border-black">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="label">Commande</p>
            <h2 className="text-2xl font-black tracking-tighter font-mono">{order.order_id}</h2>
            <p className="text-xs text-[color:var(--color-text-muted)] mt-1">{(order.created_at || "").slice(0, 16).replace("T", " · ")}</p>
          </div>
          <button onClick={onClose} className="text-3xl leading-none" data-testid="close-drawer">×</button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
          <div className="border border-black/20 p-3"><p className="label text-xs">Statut</p><p className="font-bold">{STATUS_LABELS[order.status] || order.status}</p></div>
          <div className="border border-black/20 p-3"><p className="label text-xs">Montant</p><p className="font-bold">{(order.amount || 0).toFixed(2)} €</p></div>
          <div className="border border-black/20 p-3"><p className="label text-xs">Acheteur</p><p className="text-xs">{order.buyer?.name}<br/>{order.buyer?.email}</p></div>
          <div className="border border-black/20 p-3"><p className="label text-xs">Vendeur(s)</p><p className="text-xs">{(order.sellers || []).map(s => `${s?.name || "?"} (${s?.email || ""})`).join(" · ")}</p></div>
        </div>

        <section className="mb-8">
          <h3 className="font-black mb-3 text-lg">Expédition</h3>
          <div className="space-y-3">
            <div>
              <label className="label block mb-1">Transporteur</label>
              <input value={carrier} onChange={(e) => setCarrier(e.target.value)} className="input w-full" data-testid="carrier-input"/>
            </div>
            <div>
              <label className="label block mb-1">N° de suivi</label>
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} className="input w-full font-mono" placeholder="ex: 12345678ABC" data-testid="tracking-input"/>
              {tracking && carrier?.toLowerCase().includes("mondial") && (
                <a href={`https://www.mondialrelay.fr/suivi-de-colis?numeroExpedition=${encodeURIComponent(tracking)}`} target="_blank" rel="noreferrer" className="text-xs underline mt-1 inline-flex items-center gap-1">
                  Suivre sur Mondial Relay <ExternalLink size={10} strokeWidth={3}/>
                </a>
              )}
            </div>
            <div>
              <label className="label block mb-1">Changer le statut</label>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="input w-full" data-testid="new-status-select">
                <option value="">— Garder ({STATUS_LABELS[order.status]}) —</option>
                <option value="escrow_paid">Payée (séquestre)</option>
                <option value="shipped">Expédiée</option>
                <option value="delivered">Livrée</option>
                <option value="escrow_received">Réceptionnée</option>
              </select>
            </div>
            <div>
              <label className="label block mb-1">Notes logistique (interne)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="input w-full" placeholder="Remarques, échanges, problèmes…" data-testid="notes-textarea"/>
            </div>
            <button onClick={save} disabled={saving} className="btn btn-primary w-full" data-testid="save-order-btn">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </section>

        <section className="mb-8 pt-6 border-t-2 border-black/10">
          <h3 className="font-black mb-3 text-lg flex items-center gap-2"><Bell size={18} strokeWidth={2.5}/> Relancer</h3>
          <div className="space-y-3">
            <select value={notifyTarget} onChange={(e) => setNotifyTarget(e.target.value)} className="input w-full" data-testid="notify-target">
              <option value="buyer">→ Acheteur ({order.buyer?.email})</option>
              <option value="seller">→ Vendeur(s)</option>
            </select>
            <input value={notifyTitle} onChange={(e) => setNotifyTitle(e.target.value)} placeholder="Titre" className="input w-full" data-testid="notify-title"/>
            <textarea value={notifyBody} onChange={(e) => setNotifyBody(e.target.value)} placeholder="Message" rows={3} className="input w-full" data-testid="notify-body"/>
            <button onClick={sendNotif} disabled={notifying} className="btn btn-sm border-2 border-black w-full bg-white hover:bg-black hover:text-white" data-testid="send-notify-btn">
              {notifying ? "Envoi…" : "Envoyer la relance"}
            </button>
          </div>
        </section>

        <section className="pt-6 border-t-2 border-black/10">
          <button onClick={onContactMr} className="btn w-full bg-[color:var(--color-coral)] text-white border-2 border-black" data-testid="open-mr-modal-btn">
            <Mail size={16} strokeWidth={3}/> Contacter Mondial Relay
          </button>
        </section>
      </div>
    </div>
  );
}

function MondialRelayModal({ order, onClose }) {
  const [reason, setReason] = useState("undelivered");
  const [tracking, setTracking] = useState(order?.tracking_number || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const REASON_OPTIONS = [
    { v: "lost", l: "Colis perdu" },
    { v: "damaged", l: "Colis endommagé" },
    { v: "undelivered", l: "Non distribué" },
    { v: "other", l: "Autre" },
  ];

  const send = async (e) => {
    e.preventDefault();
    if (message.length < 20) { toast.error("Le message doit faire au moins 20 caractères"); return; }
    setSending(true);
    try {
      await api.post(`/logistics/mondial-relay-contact`, {
        order_id: order?.order_id,
        reason, tracking_number: tracking, message,
      });
      toast.success("Email envoyé à Mondial Relay ✓");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="mr-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={send} className="bg-white border-2 border-black w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="label">Back-office</p>
            <h3 className="text-2xl font-black tracking-tighter">Contacter Mondial Relay</h3>
          </div>
          <button type="button" onClick={onClose} className="text-3xl leading-none" data-testid="close-mr-modal">×</button>
        </div>
        {order && <p className="text-xs text-[color:var(--color-text-muted)]">À propos de la commande <span className="font-mono">{order.order_id}</span></p>}
        <div>
          <label className="label block mb-1">Motif</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="input w-full" data-testid="mr-reason">
            {REASON_OPTIONS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
        <div>
          <label className="label block mb-1">N° de suivi</label>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} className="input w-full font-mono" placeholder="Tracking" data-testid="mr-tracking"/>
        </div>
        <div>
          <label className="label block mb-1">Message <span className="text-[color:var(--color-text-muted)]">({message.length}/20 min)</span></label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className="input w-full" placeholder="Bonjour, nous rencontrons un problème avec le colis suivant. Pourriez-vous nous indiquer…" required data-testid="mr-message"/>
        </div>
        <p className="text-xs text-[color:var(--color-text-muted)]">L&apos;email partira depuis noreply@fripri.com avec votre adresse en reply-to. Une copie sera enregistrée dans l&apos;historique.</p>
        <button type="submit" disabled={sending} className="btn btn-primary w-full" data-testid="mr-send-btn">
          {sending ? "Envoi…" : "Envoyer à Mondial Relay"}
        </button>
      </form>
    </div>
  );
}
